import React, { useState, useEffect } from 'react';
import { FaMapPin } from 'react-icons/fa';
import { FaGlobe, FaInstagram } from 'react-icons/fa6';
import axiosPost from '../helpers/axios-helper';
import '../styles/CountryOrganisations.css';
import { Link } from 'react-router-dom';
import { slugify } from '../constants/venueTypes';

/**
 * LGBTQ+ organisations working in a destination, managed from the dashboard.
 * The section stays hidden for a country with none listed.
 */

/**
 * Initials for the monogram shown when an organisation has no logo file yet.
 * Two letters at most: three or more stop reading as a mark.
 */
const initialsOf = (name) => String(name || '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(word => word[0].toUpperCase())
  .join('');

/**
 * A stable tint per organisation, so the monograms differ from each other but
 * never change between visits. Derived from the name rather than the index, so
 * reordering the list does not reshuffle the colours.
 */
const TINTS = ['#efedf4', '#f1efe7', '#e9f1ee', '#f4eef0', '#eceff4'];
const tintFor = (name) => {
  const sum = String(name || '')
    .split('')
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return TINTS[sum % TINTS.length];
};

export default function CountryOrganisations({ countryId, countryName }) {
  const [organisations, setOrganisations] = useState([]);

  useEffect(() => {
    if (!countryId) return undefined;
    const controller = new AbortController();
    axiosPost('organisations/by-country', { 'Content-Type': 'application/json' }, { country_id: countryId }, (resp) => {
      setOrganisations(resp?.success ? (resp.data.items || []) : []);
    }, controller);
    return () => controller.abort();
  }, [countryId]);

  if (organisations.length === 0) return null;

  return (
    <>
      <div className='country-section-header'>LGBTQ+ Organisations</div>

      <div className='orgs-intro'>
        Groups working on the ground in {countryName || 'this destination'}. Worth
        knowing before you travel, and worth supporting while you are there.
      </div>

      <div className='orgs-grid'>
        {organisations.map((org) => (
          <article className='org-card' key={org.id}>
            <Link className='org-card-link' to={`/organisation/${org.id}-${slugify(org.name)}`} aria-label={`About ${org.name}`} />
            <div className='org-card-head'>
              {org.logo ? (
                <img className='org-logo' src={org.logo} alt={`${org.name} logo`} />
              ) : (
                <span
                  className='org-logo org-logo-monogram'
                  style={{ background: tintFor(org.name) }}
                  aria-hidden='true'
                >
                  {initialsOf(org.name)}
                </span>
              )}

              <div className='org-card-titles'>
                <h3 className='org-name'>{org.name}</h3>
                {org.focus && <span className='org-focus'>{org.focus}</span>}
              </div>
            </div>

            {org.city && (
              <p className='org-city'>
                <FaMapPin size={11} />
                {org.city}
              </p>
            )}

            <p className='org-summary'>{org.summary}</p>

            <div className='org-links'>
              {org.website && (
                <a href={org.website} target='_blank' rel='noreferrer'>
                  <FaGlobe size={13} /> Website
                </a>
              )}
              {org.instagram && (
                <a href={org.instagram} target='_blank' rel='noreferrer'>
                  <FaInstagram size={13} /> Instagram
                </a>
              )}
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
